import type { Chapter } from '@/types/storytelling'

export const theHook: Chapter = {
  id: 'the-hook',
  title: 'The Hook',
  hook: 'Why do some stories pull you in instantly?',
  payoff: 'Learn how stories grab attention from the very first line',
  steps: [
    {
      type: 'feel',
      storyMoment:
        'The letter arrived on a Tuesday, slipped under the door while everyone was asleep. It had no stamp. No return address. Just one word on the envelope — her name.',
      question: 'What just happened inside you?',
      reflectionChoices: [
        { emoji: '🤔', text: 'I started wondering — who sent it?' },
        { emoji: '😬', text: 'I felt a little nervous for her' },
        { emoji: '😮', text: 'My brain was already making up what the letter says' },
      ],
    },
    {
      type: 'shape',
      setup:
        'A kid named Ava finds something strange in her backpack after school. It wasn\'t there this morning.',
      prompt: 'Which opening hooks you the most?',
      choices: [
        {
          id: 'a',
          text: 'Start with Ava\'s morning',
          preview: '"Ava woke up at 7:15 like she always did. She ate cereal, brushed her teeth, and grabbed her backpack."',
        },
        {
          id: 'b',
          text: 'Start with the discovery',
          preview: '"Ava unzipped her backpack and froze. Sitting on top of her math folder was a small glass jar — filled with something that was glowing."',
        },
        {
          id: 'c',
          text: 'Start with a question',
          preview: '"Later, Ava would wonder how long the jar had been in there. If it had been watching her all day. But right now, all she could do was stare."',
        },
      ],
    },
    {
      type: 'see',
      outcomes: {
        a: {
          narrative:
            'Starting with the morning routine makes us wait. We get details we don\'t need yet. By the time Ava finds the jar, we\'ve already started to drift.',
          reflection: 'The reader had to wait too long for the interesting part. What happens when a story makes you wait?',
        },
        b: {
          narrative:
            'Starting with the discovery drops us right into the moment. We don\'t know Ava yet — but we don\'t need to. The glowing jar is doing all the work. We want to know what it is.',
          reflection: 'You went straight to the most interesting moment. Why does that work?',
        },
        c: {
          narrative:
            'Starting with hindsight is a power move. "Later, Ava would wonder..." tells us something big is about to happen. We don\'t know what yet — but we know it matters.',
          reflection: 'You gave the reader a promise that something matters, without saying what. That\'s a different kind of hook.',
        },
      },
    },
    {
      type: 'secret',
      revealLine: 'That pull you felt? That\'s a hook.',
      principle:
        'A hook doesn\'t explain — it makes the reader curious. Start with the moment that matters, not the moments before it.',
      writerBridge:
        'Writers use hooks to grab readers from the very first line. Start with the interesting part.',
    },
  ],
  unlocksPlayground: 'branches',
  secret: {
    id: 'the-hook',
    title: 'The Hook',
    principle:
      'A hook doesn\'t explain — it makes the reader curious. Start with the moment that matters, not the moments before it.',
    metadata: {
      category: 'structure',
      triggerContexts: ['opening'],
    },
  },
}
